import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { 
  MessageCircle, 
  Mail, 
  Phone,
  CheckCircle2,
  Loader2,
  Send,
  MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "56929894282";
const WHATSAPP_SUCCESS_MESSAGE = encodeURIComponent("Hola, acabo de enviar el formulario de contacto y quiero coordinar una visita.");
const WHATSAPP_SUCCESS_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_SUCCESS_MESSAGE}`;
const WHATSAPP_MESSAGE = encodeURIComponent("Hola, quiero agendar evaluación.");
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
const EMAIL = "contacto@odontofix.cl";
const PHONE = "+56 9 2989 4282";

const equipmentTypes = [
  "Sillón dental",
  "Autoclave",
  "Rayos X",
  "Compresor",
  "Ultrasonido",
  "Otros"
];

const serviceTypes = [
  { value: "Preventiva", label: "Mantención Preventiva (MP)" },
  { value: "Correctiva", label: "Mantención Correctiva (MC)" },
  { value: "Repuestos", label: "Cotización de Repuestos" },
  { value: "Seguimiento/App", label: "Seguimiento / App Gratuita" }
];

const contactSchema = z.object({
  nombre: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  clinica: z.string().min(2, "Ingresa el nombre de tu clínica o empresa"),
  ciudad: z.string().min(2, "Ingresa tu ciudad"),
  telefono: z.string().min(8, "Ingresa un teléfono válido"),
  email: z.string().email("Ingresa un email válido"),
  tipo_equipo: z.string().min(1, "Selecciona un tipo de equipo"),
  servicio_requerido: z.string().min(1, "Selecciona un servicio"),
  mensaje: z.string().min(10, "El mensaje debe tener al menos 10 caracteres"),
  acepta_contacto: z.boolean().refine(val => val === true, {
    message: "Debes aceptar ser contactado"
  })
});

export default function Contacto() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const defaultService = searchParams.get("asunto") || "";

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nombre: "",
      clinica: "",
      ciudad: "",
      telefono: "",
      email: "",
      tipo_equipo: "",
      servicio_requerido: defaultService,
      mensaje: "",
      acepta_contacto: false
    }
  });

  const watchAccepta = watch("acepta_contacto");

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setIsSuccess(true);
      toast.success("¡Mensaje enviado correctamente!");
      reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error al enviar el formulario. Intenta nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contacto-page">
      {/* Hero Section */}
      <section className="bg-[#384091] py-16 md:py-20" data-testid="contacto-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Contáctanos
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Escríbenos para cotizar mantención preventiva o correctiva de tus equipos clínicos
            </p>
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 bg-white" data-testid="contact-cards">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {/* WhatsApp Card */}
            <Card className="bg-[#25D366]/5 border-[#25D366]/20 hover:shadow-lg hover:border-[#25D366]/40 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-7 w-7 text-[#25D366]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#1F2937] mb-1">
                  WhatsApp
                </h3>
                <p className="text-[#4B5563] text-sm mb-4">{PHONE}</p>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-[#25D366] hover:bg-[#25D366]/90 text-white w-full h-11 font-medium"
                    data-testid="contact-whatsapp-btn"
                  >
                    Escribir ahora
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="bg-[#0FB4B7]/5 border-[#0FB4B7]/20 hover:shadow-lg hover:border-[#0FB4B7]/40 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#0FB4B7]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-[#0FB4B7]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#1F2937] mb-1">
                  Correo
                </h3>
                <p className="text-[#4B5563] text-sm mb-4">{EMAIL}</p>
                <a href={`mailto:${EMAIL}`}>
                  <Button 
                    className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white w-full h-11 font-medium"
                    data-testid="contact-email-btn"
                  >
                    Enviar correo
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="bg-[#384091]/5 border-[#384091]/20 hover:shadow-lg hover:border-[#384091]/40 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#384091]/10 flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-7 w-7 text-[#384091]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#1F2937] mb-1">
                  Teléfono
                </h3>
                <p className="text-[#4B5563] text-sm mb-4">{PHONE}</p>
                <a href={`tel:${PHONE.replace(/\s/g, '')}`}>
                  <Button 
                    className="bg-[#384091] hover:bg-[#2D3375] text-white w-full h-11 font-medium"
                    data-testid="contact-phone-btn"
                  >
                    Llamar ahora
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-20 bg-[#F5F7FA]" data-testid="contact-form-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1F2937] mb-3">
                Formulario de contacto
              </h2>
              <p className="text-[#4B5563]">
                Completa el formulario y te contactaremos a la brevedad
              </p>
            </div>

            {isSuccess ? (
              <Card className="bg-white border-0 shadow-xl" data-testid="success-message">
                <CardContent className="p-8 md:p-10 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#1F2937] mb-3">
                    ¡Mensaje enviado!
                  </h3>
                  <p className="text-[#4B5563] mb-8 leading-relaxed">
                    Recibimos tu solicitud y te contactaremos pronto.<br />
                    Si deseas respuesta inmediata, escríbenos por WhatsApp.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href={WHATSAPP_SUCCESS_URL} target="_blank" rel="noopener noreferrer">
                      <Button 
                        size="lg"
                        className="bg-[#25D366] hover:bg-[#25D366]/90 text-white h-12 px-6 font-medium"
                        data-testid="success-whatsapp-btn"
                      >
                        <MessageCircle className="h-5 w-5 mr-2" />
                        Escribir por WhatsApp ahora
                      </Button>
                    </a>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="h-12 px-6 font-medium border-gray-300"
                      onClick={() => setIsSuccess(false)}
                      data-testid="new-form-btn"
                    >
                      Enviar otro mensaje
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-0 shadow-xl">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Nombre */}
                      <div className="space-y-2">
                        <Label htmlFor="nombre" className="text-[#1F2937] font-medium">
                          Nombre y Apellido <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="nombre"
                          placeholder="Tu nombre completo"
                          {...register("nombre")}
                          className={`h-11 ${errors.nombre ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                          data-testid="input-nombre"
                        />
                        {errors.nombre && (
                          <p className="text-sm text-red-500">{errors.nombre.message}</p>
                        )}
                      </div>

                      {/* Clínica */}
                      <div className="space-y-2">
                        <Label htmlFor="clinica" className="text-[#1F2937] font-medium">
                          Clínica / Empresa <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="clinica"
                          placeholder="Nombre de tu clínica"
                          {...register("clinica")}
                          className={`h-11 ${errors.clinica ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                          data-testid="input-clinica"
                        />
                        {errors.clinica && (
                          <p className="text-sm text-red-500">{errors.clinica.message}</p>
                        )}
                      </div>

                      {/* Ciudad */}
                      <div className="space-y-2">
                        <Label htmlFor="ciudad" className="text-[#1F2937] font-medium">
                          Ciudad <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ciudad"
                          placeholder="Tu ciudad"
                          {...register("ciudad")}
                          className={`h-11 ${errors.ciudad ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                          data-testid="input-ciudad"
                        />
                        {errors.ciudad && (
                          <p className="text-sm text-red-500">{errors.ciudad.message}</p>
                        )}
                      </div>

                      {/* Teléfono */}
                      <div className="space-y-2">
                        <Label htmlFor="telefono" className="text-[#1F2937] font-medium">
                          Teléfono <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="telefono"
                          type="tel"
                          placeholder="+56 9 1234 5678"
                          {...register("telefono")}
                          className={`h-11 ${errors.telefono ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                          data-testid="input-telefono"
                        />
                        {errors.telefono && (
                          <p className="text-sm text-red-500">{errors.telefono.message}</p>
                        )}
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1F2937] font-medium">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="tu@email.com"
                          {...register("email")}
                          className={`h-11 ${errors.email ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                          data-testid="input-email"
                        />
                        {errors.email && (
                          <p className="text-sm text-red-500">{errors.email.message}</p>
                        )}
                      </div>

                      {/* Tipo de equipo */}
                      <div className="space-y-2">
                        <Label className="text-[#1F2937] font-medium">
                          Tipo de equipo <span className="text-red-500">*</span>
                        </Label>
                        <Select 
                          onValueChange={(value) => setValue("tipo_equipo", value)}
                        >
                          <SelectTrigger 
                            className={`h-11 ${errors.tipo_equipo ? "border-red-500" : ""}`}
                            data-testid="select-tipo-equipo"
                          >
                            <SelectValue placeholder="Selecciona un equipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {equipmentTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.tipo_equipo && (
                          <p className="text-sm text-red-500">{errors.tipo_equipo.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Servicio requerido */}
                    <div className="space-y-2">
                      <Label className="text-[#1F2937] font-medium">
                        Servicio requerido <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        onValueChange={(value) => setValue("servicio_requerido", value)}
                        defaultValue={defaultService}
                      >
                        <SelectTrigger 
                          className={`h-11 ${errors.servicio_requerido ? "border-red-500" : ""}`}
                          data-testid="select-servicio"
                        >
                          <SelectValue placeholder="Selecciona un servicio" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.servicio_requerido && (
                        <p className="text-sm text-red-500">{errors.servicio_requerido.message}</p>
                      )}
                    </div>

                    {/* Mensaje */}
                    <div className="space-y-2">
                      <Label htmlFor="mensaje" className="text-[#1F2937] font-medium">
                        Mensaje <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="mensaje"
                        placeholder="Cuéntanos sobre tu equipo, la falla o el servicio que necesitas..."
                        rows={4}
                        {...register("mensaje")}
                        className={`resize-none ${errors.mensaje ? "border-red-500 focus:ring-red-500" : "focus:ring-[#0FB4B7]"}`}
                        data-testid="input-mensaje"
                      />
                      {errors.mensaje && (
                        <p className="text-sm text-red-500">{errors.mensaje.message}</p>
                      )}
                    </div>

                    {/* Checkbox */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="acepta_contacto"
                        checked={watchAccepta}
                        onCheckedChange={(checked) => setValue("acepta_contacto", checked)}
                        className="mt-0.5 data-[state=checked]:bg-[#0FB4B7] data-[state=checked]:border-[#0FB4B7]"
                        data-testid="checkbox-acepta"
                      />
                      <Label 
                        htmlFor="acepta_contacto" 
                        className="text-sm text-[#4B5563] cursor-pointer leading-relaxed"
                      >
                        Acepto ser contactado por Odontofix para responder mi consulta
                      </Label>
                    </div>
                    {errors.acepta_contacto && (
                      <p className="text-sm text-red-500">{errors.acepta_contacto.message}</p>
                    )}

                    {/* Submit */}
                    <Button 
                      type="submit" 
                      size="lg"
                      className="w-full bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-12 font-medium text-base shadow-sm hover:shadow-md transition-all"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Enviar mensaje
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-center gap-2 mt-6 text-sm text-[#4B5563]">
              <MapPin className="h-4 w-4" />
              <span>Atendemos Santiago y regiones (según coordinación)</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
